import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
         AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, 
         AlertDialogAction } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/useToast';
import { NormalizedAction } from '@/types/action-types';
import { KeywordList } from './KeywordList';
import { BudgetDetail } from './BudgetDetail';
import { GenericDetail } from './GenericDetail';
import { useUndoAction } from '@/lib/api-extensions/action-queries';

interface TaskDetailModalProps {
  action: NormalizedAction | null;
  open: boolean;
  onClose: () => void;
  selectedKeywordId?: number;
  onSelectKeyword?: (keywordId: number) => void;
  onActionUndone?: () => void;
}

export function TaskDetailModal({
  action,
  open,
  onClose,
  selectedKeywordId,
  onSelectKeyword,
  onActionUndone
}: TaskDetailModalProps) {
  // Only two state variables needed
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  const { toast } = useToast();
  const { mutate: undoAction, isPending: isUndoing } = useUndoAction();
  
  
  if (!action) return null;
  
  // Handle action revert
  const handleUndo = async () => {
    try {
      await undoAction(action.id);
      toast({
        title: 'Success',
        description: 'Changes have been reverted successfully',
      });
      
      if (onActionUndone) {
        onActionUndone();
      }
      
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to revert action: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setConfirmDialogOpen(false);
    }
  };
  
  // Render the appropriate detail component based on action type
  const renderDetailContent = () => {
    switch (action.actionType) {
      case 'ADD_KEYWORDS':
      case 'ADD_EXACT_MATCH_KEYWORD':
      case 'PAUSE_KEYWORDS':
      case 'DELETE_KEYWORDS':
      case 'ADD_NEGATIVE_KEYWORD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_AD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN':
        return (
          <KeywordList
            action={action}
            selectedKeywordId={selectedKeywordId}
            onSelectKeyword={onSelectKeyword}
            isModal={true}
          />
        );
        
      case 'SET_BUDGET':
        return <BudgetDetail action={action} isModal={true} />;
        
      default:
        return <GenericDetail action={action} isModal={true} />;
    }
  };
  
  return (
    <>
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Revert Action</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revert this action? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUndo} className="bg-[#290BD9] hover:bg-[#2309C5]">
              {isUndoing ? 'Reverting...' : 'Confirm Revert'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Main Modal */}
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{action.reason || getActionTitle(action)}</DialogTitle>
          </DialogHeader>
          
          {renderDetailContent()}
          
          <DialogFooter className="mt-4">
            <Button variant="default" className="bg-[#290BD9] hover:bg-[#2309C5]" onClick={onClose}>
              Close
            </Button>
            {!action.isUndone && (
              <Button 
                onClick={() => setConfirmDialogOpen(true)} 
                disabled={isUndoing} 
                variant="outline"
              >
                {isUndoing ? 'Reverting...' : 'Revert All Changes'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Generate a title for the action if no reason is provided
 */
function getActionTitle(action: NormalizedAction): string {
  switch (action.actionType) {
    case 'ADD_KEYWORDS':
      return 'Added Keywords';
    case 'PAUSE_KEYWORDS':
      return 'Paused Keywords';
    case 'DELETE_KEYWORDS':
      return 'Removed Keywords';
    case 'SET_BUDGET':
      return 'Updated Campaign Budget';
    default:
      return action.actionType.replace(/_/g, ' ');
  }
}